import axios from 'axios';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import analytics from '../services/analytics';
import authClient, { AUTH_TOKEN_KEY } from '../services/auth-client';

import { API_URL, AUTH_URL } from '../constants/urls';

interface User {
  id: string;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  is_active: number;
  is_superuser: number;
  role?: string;
  role_title?: string;
  role_description?: string;
  organization_name?: string;
  // GenAI access status
  can_use_genai?: boolean;
  organization_plan?: string;
  is_trial_active?: boolean;
  image?: string;
}

interface UpgradePrompt {
  feature: string;
  message: string;
  plan_required: string;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  upgradePrompt: UpgradePrompt | null;
  login: (
    email_param: string,
    password_param: string,
    rememberMe?: boolean,
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  showUpgradeModal: (prompt: UpgradePrompt) => void;
  hideUpgradeModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to get stored token
const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

// Helper function to store token
const storeToken = (token: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

// Helper function to clear token
const removeToken = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

// Helper function to redirect to Auth Portal
const redirectToAuthPortal = (returnUrl?: string): void => {
  const currentUrl = returnUrl || window.location.href;
  window.location.href = `${AUTH_URL}/login?redirect=${encodeURIComponent(currentUrl)}`;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<
    'checking' | 'authenticated' | 'unauthenticated'
  >('checking');
  const [upgradePrompt, setUpgradePrompt] = useState<UpgradePrompt | null>(
    null,
  );

  const redirectingRef = useRef(false);
  const authStatusRef = useRef(authStatus);
  const isLoadingRef = useRef(isLoading);

  useEffect(() => {
    authStatusRef.current = authStatus;
  }, [authStatus]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Check Better Auth session and fetch user data
  const checkSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setAuthStatus('checking');

    try {
      // 1) Fast-path: if we already have a token, validate it against Spotlight.
      // This avoids redirect loops when Better Auth session checks are temporarily unavailable.
      const storedToken = getStoredToken();
      if (storedToken) {
        setToken(storedToken);
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          setUser((prev) => ({
            ...(prev || {
              id: response.data.id,
              email: response.data.email,
            }),
            ...response.data,
          }));
          setAuthStatus('authenticated');
          return;
        } catch {
          // Token is invalid/expired: clear and fall through to session check
          setToken(null);
          setUser(null);
          removeToken();
        }
      }

      // 2) Fallback: Use Better Auth to check session (cookie-based).
      const { data: session } = await authClient.getSession();

      if (!session?.user) {
        setToken(null);
        setUser(null);
        removeToken();
        setAuthStatus('unauthenticated');
        return;
      }

      const nextToken = session.session?.token || getStoredToken();
      if (!nextToken) {
        setToken(null);
        setUser(null);
        removeToken();
        setAuthStatus('unauthenticated');
        return;
      }

      storeToken(nextToken);
      setToken(nextToken);

      setUser({
        id: session.user.id,
        email: session.user.email,
        name: session.user.name,
        first_name: (session.user as any).first_name,
        last_name: (session.user as any).last_name,
        is_active: 1,
        is_superuser: 0,
        image: session.user.image ?? undefined,
      });

      // Best-effort hydration from API (non-blocking for auth).
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${nextToken}` },
        });
        setUser((prev) =>
          prev ? { ...prev, ...response.data } : response.data,
        );
      } catch {
        // Ignore: backend may not be ready or user may not be synced yet.
      }

      setAuthStatus('authenticated');
    } catch (err) {
      setToken(null);
      setUser(null);
      removeToken();
      setAuthStatus('unauthenticated');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check session on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (isLoading) return;
    if (authStatus !== 'unauthenticated') return;
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    redirectToAuthPortal();
  }, [authStatus, isLoading]);

  // Set up axios interceptors for API requests
  useEffect(() => {
    // Request interceptor to add auth token
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const currentToken = getStoredToken();
        if (currentToken && config.url && !config.url.includes('/auth/token')) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        // Add timing for analytics tracking
        (config as any)._startTime = Date.now();
        // Track the API request
        analytics.trackApiRequest(config);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor to handle token expiration
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // Track successful API call timing
        const startTime = (response.config as any)._startTime || Date.now();
        analytics.trackApiResponse(response, startTime);
        return response;
      },
      (error) => {
        // Track failed API call
        analytics.trackApiError(error);

        if (error.response) {
          const errorData = error.response.data;

          // Format error based on error type (preserved for consistency)
          if (
            errorData?.error_type === 'validation_error' &&
            errorData.errors
          ) {
            errorData.errors.forEach((err: any) => {
              if (err.input) {
                // Validation error handling
              }
            });
          }

          // Handle 401 Unauthorized - redirect to Auth Portal
          if (error.response.status === 401) {
            setToken(null);
            setUser(null);
            removeToken();
            if (authStatusRef.current !== 'checking' && !isLoadingRef.current) {
              setAuthStatus('unauthenticated');
            }
          }

          // Handle 402 Payment Required responses (upgrade needed)
          if (error.response.status === 402) {
            const responseData = error.response.data;
            setUpgradePrompt({
              feature: responseData.feature || 'Premium Feature',
              message:
                responseData.message || 'This feature requires an upgrade',
              plan_required: responseData.plan_required || 'Starter',
            });
          }
        }

        return Promise.reject(error);
      },
    );

    // Clean up interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Login - redirects to Auth Portal (no login in Spotlight)
  const login = async () => {
    redirectToAuthPortal();
  };

  // Logout - clear tokens and redirect to Auth Portal
  const logout = async () => {
    try {
      // Sign out from Better Auth
      await authClient.signOut();
    } catch {
      // Continue with local logout even if Better Auth signOut fails
    }

    setToken(null);
    setUser(null);
    setError(null);
    removeToken();

    // Redirect to Auth Portal
    window.location.href = AUTH_URL;
  };

  // Refresh user data from backend
  const refreshUserData = async () => {
    const currentToken = getStoredToken();
    if (currentToken) {
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        setUser((prevUser) =>
          prevUser
            ? {
                ...prevUser,
                ...response.data,
              }
            : null,
        );
      } catch {
        // Error refreshing user data
      }
    }
  };

  const showUpgradeModal = (prompt: UpgradePrompt) => {
    setUpgradePrompt(prompt);
  };

  const hideUpgradeModal = () => {
    setUpgradePrompt(null);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isLoading,
        error,
        upgradePrompt,
        login,
        logout,
        refreshUserData,
        showUpgradeModal,
        hideUpgradeModal,
      }}
    >
      {isLoading || authStatus === 'unauthenticated' ? (
        <div className="min-h-screen flex items-center justify-center">
          <div style={{ fontSize: 14, opacity: 0.7 }}>Loading…</div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
